import React, { useEffect, useState } from 'react';
import { useIframeStore } from '../../../../store/useIframe';

/**
 * @author Claude
 * @date 2025.11.04
 * @description 템플릿 승인 대기 화면
 */

interface Step10TemplateWaitingPageProps {
  allTemplatesApproved: boolean;
  onTemplatesApproved: () => void;
}

const Step10TemplateWaitingPage: React.FC<Step10TemplateWaitingPageProps> = ({ 
  allTemplatesApproved,
  onTemplatesApproved 
}) => {
  const { closeIframe } = useIframeStore();
  const [showPulse, setShowPulse] = useState(true);

  useEffect(() => {
    // iframe 닫기
    closeIframe();
  }, [closeIframe]);

  // 승인 완료 체크는 별도 useEffect로 분리하고 한 번만 실행
  useEffect(() => {
    if (allTemplatesApproved) {
      onTemplatesApproved();
    }
  }, [allTemplatesApproved]); // onTemplatesApproved 의존성 제거

  useEffect(() => {
    // 펄스 애니메이션 토글
    const interval = setInterval(() => {
      setShowPulse(prev => !prev);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ 
      height: '100vh',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* 배경 원형 효과 */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
        animation: 'float 6s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '15%',
        right: '15%',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.08)',
        animation: 'float 8s ease-in-out infinite reverse'
      }} />

      {/* 메인 콘텐츠 */}
      <div style={{
        textAlign: 'center',
        maxWidth: '700px',
        width: '100%',
        position: 'relative',
        zIndex: 10
      }}>
        {/* 대기 아이콘 */}
        <div style={{
          fontSize: '100px',
          marginBottom: '30px',
          animation: showPulse ? 'bounce 1.5s ease-in-out infinite' : 'none',
          transition: 'all 0.3s ease'
        }}>
          ⏳
        </div>

        {/* 메인 타이틀 */}
        <h1 style={{
          fontSize: '48px',
          fontWeight: '900',
          color: 'white',
          marginBottom: '20px',
          lineHeight: '1.2',
          textShadow: '3px 3px 6px rgba(0,0,0,0.3)',
          letterSpacing: '-1px'
        }}>
          템플릿 승인 대기중
        </h1>

        <h2 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: 'rgba(255, 255, 255, 0.9)',
          marginBottom: '40px',
          lineHeight: '1.4'
        }}>
          승인까지 <strong style={{
            color: '#FFD700',
            textShadow: '2px 2px 4px rgba(0,0,0,0.4)',
            fontSize: '32px'
          }}>약 1 영업일</strong> 정도 소요됩니다
        </h2>

        {/* 새로고침 버튼 */}
        <button
          onClick={() => window.location.reload()}
          style={{
            fontSize: '20px',
            fontWeight: '700',
            padding: '18px 45px',
            background: 'linear-gradient(135deg, #FEE500 0%, #FFD700 100%)',
            color: '#2c3e50',
            border: 'none',
            borderRadius: '15px',
            cursor: 'pointer',
            boxShadow: '0 8px 25px rgba(255, 229, 0, 0.4)',
            transition: 'all 0.3s ease',
            transform: 'scale(1)',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            margin: '0 auto',
            textShadow: 'none'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 12px 35px rgba(255, 229, 0, 0.6)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 229, 0, 0.4)';
          }}
        >
          <span>🔄</span>
          <span>승인 상태 새로고침</span>
        </button>
      </div>

      {/* CSS 애니메이션을 위한 스타일 */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes bounce {
              0%, 20%, 50%, 80%, 100% {
                transform: translateY(0);
              }
              40% {
                transform: translateY(-15px);
              }
              60% {
                transform: translateY(-8px);
              }
            }

            @keyframes float {
              0%, 100% {
                transform: translateY(0px) rotate(0deg);
              }
              50% {
                transform: translateY(-20px) rotate(180deg);
              }
            }

            @keyframes pulse {
              0%, 100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.7;
                transform: scale(1.05);
              }
            }
          `
        }}
      />
    </div>
  );
};

export default Step10TemplateWaitingPage;